/**
 * 在庫管理APIサービス
 */

import { apiClient } from '@/shared/lib/axios';
import { logger } from '@/shared/lib/logger';
import { DateFactory } from '@/shared/utils/dateFactory';
import { authService } from '@/features/auth/services/authService';
import type {
  MaterialType,
  SiteInventoryData,
  InventoryItem,
  InventoryAdjustmentRequest,
  ConsumeRequest,
  UpdateThresholdsRequest,
  TransactionResult,
  TransactionHistoryResult,
  TransactionHistoryQuery,
  TransactionHistoryItem,
  InventoryTransactionType
} from '../types/inventory';
import { INVENTORY_TRANSACTION_TYPE } from '../types/inventory';

// ========== APIレスポンス型定義 ==========

interface ApiInventoryItem {
  readonly materialTypeId: number;
  readonly materialTypeCode: string;
  readonly materialTypeName: string;
  readonly currentQuantity: number;
  readonly minimumThreshold: number;
  readonly reorderThreshold: number;
  readonly isLowStock: boolean;
  readonly isReorderRequired: boolean;
  readonly lastTransactionAt: string | null;
  readonly lastTransactionUserName: string | null;
  readonly pendingOrderedQuantity: number;
  readonly effectiveQuantity: number;
  readonly hasOverdueOrders: boolean;
}

interface ApiSiteInventoryData {
  readonly siteId: number;
  readonly siteName: string;
  readonly inventories: readonly ApiInventoryItem[];
}

interface GetInventoryResponse {
  readonly success: boolean;
  readonly inventory: ApiSiteInventoryData;
}

interface GetAllInventoriesResponse {
  readonly success: boolean;
  readonly inventories: readonly ApiSiteInventoryData[];
}

interface GetMaterialTypesResponse {
  readonly success: boolean;
  readonly materialTypes: readonly MaterialType[];
}

interface ApiTransactionResult {
  readonly transactionId: number;
  readonly siteId: number;
  readonly panelId: number | null;
  readonly materialTypeId: number;
  readonly materialTypeName: string;
  readonly transactionType: string;
  readonly quantity: number;
  readonly quantityBefore: number;
  readonly quantityAfter: number;
  readonly reasonCode: string;
  readonly reasonDisplayName: string;
  readonly note: string | null;
  readonly performedByUserName: string;
  readonly createdAt: string;
  readonly currentInventory: {
    readonly currentQuantity: number;
    readonly isLowStock: boolean;
    readonly isReorderRequired: boolean;
  };
}

interface TransactionResponse {
  readonly success: boolean;
  readonly transaction: ApiTransactionResult;
  readonly currentInventory: {
    readonly currentQuantity: number;
    readonly isLowStock: boolean;
    readonly isReorderRequired: boolean;
  };
}

interface UpdateThresholdsResponse {
  readonly success: boolean;
  readonly inventory: {
    readonly siteId: number;
    readonly materialTypeId: number;
    readonly currentQuantity: number;
    readonly minimumThreshold: number;
    readonly reorderThreshold: number;
    readonly isLowStock: boolean;
    readonly isReorderRequired: boolean;
  };
}

interface ApiTransactionHistoryItem {
  readonly id: number;
  readonly materialTypeId: number;
  readonly materialTypeName: string;
  readonly transactionType: string;
  readonly quantity: number;
  readonly quantityBefore: number;
  readonly quantityAfter: number;
  readonly reasonCode: string;
  readonly reasonDisplayName: string;
  readonly note: string | null;
  readonly performedByUserName: string;
  readonly createdAt: string;
}

interface GetTransactionHistoryResponse {
  readonly success: boolean;
  readonly transactions: readonly ApiTransactionHistoryItem[];
  readonly pagination: {
    readonly currentPage: number;
    readonly totalPages: number;
    readonly totalCount: number;
    readonly hasNextPage: boolean;
    readonly hasPreviousPage: boolean;
  };
}

// ========== ヘルパー関数 ==========

const convertInventoryItem = (item: ApiInventoryItem): InventoryItem => ({
  ...item,
  lastTransactionAt: item.lastTransactionAt
    ? DateFactory.fromISOString(item.lastTransactionAt)
    : null
});

const convertSiteInventoryData = (data: ApiSiteInventoryData): SiteInventoryData => ({
  ...data,
  inventories: data.inventories.map(convertInventoryItem)
});

const convertTransactionResult = (result: ApiTransactionResult): TransactionResult => ({
  ...result,
  transactionType: assertInventoryTransactionType(result.transactionType),
  createdAt: DateFactory.fromISOString(result.createdAt)
});

const convertTransactionHistoryItem = (item: ApiTransactionHistoryItem): TransactionHistoryItem => ({
  ...item,
  transactionType: assertInventoryTransactionType(item.transactionType),
  createdAt: DateFactory.fromISOString(item.createdAt)
});

/**
 * `transactionType` 文字列を `InventoryTransactionType` ユニオンに narrowing する型ガード。
 * 想定外値が来た場合は実行時エラーで検出する。
 */
function assertInventoryTransactionType(value: string): InventoryTransactionType {
  if (
    value === INVENTORY_TRANSACTION_TYPE.INBOUND ||
    value === INVENTORY_TRANSACTION_TYPE.CONSUMPTION
  ) {
    return value;
  }
  throw new Error(`想定外の transactionType: ${value}`);
}

// ========== API関数 ==========

/**
 * 材料種別マスタを取得
 */
export const getMaterialTypes = async (): Promise<readonly MaterialType[]> => {
  const response = await apiClient.get<GetMaterialTypesResponse>('/api/inventory/material-types');

  if (!response.data.success) {
    throw new Error('材料種別の取得に失敗しました');
  }

  return response.data.materialTypes;
};

/**
 * 特定現場の在庫データを取得
 */
export const getInventoryBySiteId = async ({
  siteId,
}: {
  siteId: number;
}): Promise<SiteInventoryData | null> => {
  try {
    const response = await apiClient.get<GetInventoryResponse>(`/api/inventory/sites/${siteId}`);

    if (!response.data.success) {
      return null;
    }

    return convertSiteInventoryData(response.data.inventory);
  } catch (error) {
    logger.captureError(error, `inventoryApiService.getInventoryBySiteId(${siteId})`);
    throw error;
  }
};

/**
 * アクセス可能な全現場の在庫データを取得
 *
 * サイト制限ロール(SITE_USER / SITE_VIEWER)は、サーバー側で自サイトのみに絞り込まれる。
 */
export const getAllInventories = async (): Promise<readonly SiteInventoryData[]> => {
  const response = await apiClient.get<GetAllInventoriesResponse>('/api/inventory/sites');

  if (!response.data.success) {
    throw new Error('在庫データの取得に失敗しました');
  }

  return response.data.inventories.map(convertSiteInventoryData);
};

/**
 * 在庫調整(増)処理を実行
 *
 * 設計書 PR-7 で `recordInbound` からリネーム。`reasonCode` から `NEW_PURCHASE` を除外し、
 * ユーザー直接の在庫調整経路として使用する(`NEW_PURCHASE` は発注ワークフロー経由のみ)。
 * API パスは既存の `/inbound` を維持(サーバー側ルートは変更なし)。
 */
export const recordInventoryAdjustment = async ({
  siteId,
  request,
}: {
  siteId: number;
  request: InventoryAdjustmentRequest;
}): Promise<TransactionResult> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.post<TransactionResponse>(
    `/api/inventory/sites/${siteId}/inbound`,
    request,
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error('在庫調整(増)処理に失敗しました');
  }

  return convertTransactionResult(response.data.transaction);
};

/**
 * 消費処理を実行
 */
export const recordConsumption = async ({
  siteId,
  request,
}: {
  siteId: number;
  request: ConsumeRequest;
}): Promise<TransactionResult> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.post<TransactionResponse>(
    `/api/inventory/sites/${siteId}/consume`,
    request,
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error('消費処理に失敗しました');
  }

  return convertTransactionResult(response.data.transaction);
};

/**
 * 閾値設定を更新
 */
export const updateThresholds = async ({
  siteId,
  request,
}: {
  siteId: number;
  request: UpdateThresholdsRequest;
}): Promise<{
  readonly siteId: number;
  readonly materialTypeId: number;
  readonly currentQuantity: number;
  readonly minimumThreshold: number;
  readonly reorderThreshold: number;
  readonly isLowStock: boolean;
  readonly isReorderRequired: boolean;
}> => {
  const csrfToken = await authService.getCsrfToken();

  const response = await apiClient.put<UpdateThresholdsResponse>(
    `/api/inventory/sites/${siteId}/thresholds`,
    request,
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    }
  );

  if (!response.data.success) {
    throw new Error('閾値設定の更新に失敗しました');
  }

  return response.data.inventory;
};

/**
 * 操作履歴を取得
 */
export const getTransactionHistory = async ({
  siteId,
  query,
}: {
  siteId: number;
  query?: TransactionHistoryQuery;
}): Promise<TransactionHistoryResult> => {
  const params = new URLSearchParams();

  if (query?.materialTypeId !== undefined) {
    params.append('materialTypeId', String(query.materialTypeId));
  }
  if (query?.transactionType !== undefined) {
    params.append('transactionType', query.transactionType);
  }
  if (query?.startDate !== undefined) {
    params.append('startDate', query.startDate);
  }
  if (query?.endDate !== undefined) {
    params.append('endDate', query.endDate);
  }
  if (query?.page !== undefined) {
    params.append('page', String(query.page));
  }
  if (query?.limit !== undefined) {
    params.append('limit', String(query.limit));
  }

  const queryString = params.toString();
  const url = `/api/inventory/sites/${siteId}/transactions${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<GetTransactionHistoryResponse>(url);

  if (!response.data.success) {
    throw new Error('操作履歴の取得に失敗しました');
  }

  return {
    transactions: response.data.transactions.map(convertTransactionHistoryItem),
    pagination: response.data.pagination
  };
};
